import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import WordReveal from '../UI/WordReveal';
import FlipButton from '../UI/FlipButton';
import Logo from '../UI/Logo';
import PixelWordmark from '../UI/PixelWordmark';
import { LINKS } from '../../constants';

interface FooterProps {
  onNavigate: (view: 'home' | 'about' | 'experience' | 'projects' | 'certifications') => void;
}

const EXPLORE: { label: string; view: 'about' | 'experience' | 'projects' | 'certifications' }[] = [
  { label: 'About', view: 'about' },
  { label: 'Experience', view: 'experience' },
  { label: 'Projects', view: 'projects' },
  { label: 'Certifications', view: 'certifications' },
];

const SOCIALS = [
  { label: 'LinkedIn', href: LINKS.linkedin },
  { label: 'GitHub', href: LINKS.github },
  { label: 'Instagram', href: LINKS.instagram },
  { label: 'Résumé', href: LINKS.resume },
];

const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  return (
    <footer id='contact' className='bg-carbon text-chalk mt-24 sm:mt-32'>
      {/* Giant CTA */}
      <div className='max-w-7xl mx-auto px-5 sm:px-6 pt-20 sm:pt-28 pb-16 text-center'>
        <div className='label-mono text-chalk/45 mb-8 flex items-center justify-center gap-2'>
          <span className='text-accent'>[</span> 07 / Contact <span className='text-accent'>]</span>
        </div>

        <h2 className='font-display leading-[1.02] text-[clamp(1.8rem,9vw,8rem)]'>
          <WordReveal text="Let's work together" mode='mask' stagger={0.06} />
        </h2>

        <p className='mt-7 max-w-md mx-auto text-sm sm:text-base text-chalk/45 leading-relaxed'>
          Have a role in banking, product or analytics, or a process that deserves a better tool?
          My inbox is open.
        </p>

        <div className='mt-10 flex flex-wrap items-center justify-center gap-3'>
          <FlipButton
            label={LINKS.email}
            href={`mailto:${LINKS.email}`}
            variant='accent'
            size='lg'
            icon={<ArrowUpRight size={18} />}
          />
          <FlipButton
            label='Call me'
            href={LINKS.phoneHref}
            variant='outline'
            size='lg'
            className='text-chalk'
          />
        </div>

        <div className='label-mono text-chalk/45 mt-8 flex items-center justify-center gap-2'>
          <span className='relative flex h-1.5 w-1.5'>
            <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-60' />
            <span className='relative inline-flex rounded-full h-1.5 w-1.5 bg-accent' />
          </span>
          Available for full-time opportunities
        </div>
      </div>

      {/* Link columns */}
      <div className='border-t border-chalk/10'>
        <div className='max-w-7xl mx-auto px-5 sm:px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10'>
          <div className='col-span-2 md:col-span-1'>
            <Logo className='text-lg' />
            <p className='mt-3 text-sm text-chalk/45 leading-relaxed max-w-[230px]'>
              Business transformation, banking & analytics, from inside the workflow.
            </p>
          </div>

          <div>
            <div className='label-mono text-chalk/45 mb-4'>Explore</div>
            <nav className='flex flex-col gap-2.5'>
              {EXPLORE.map(item => (
                <button
                  key={item.view}
                  onClick={() => onNavigate(item.view)}
                  className='group w-fit text-left text-sm text-chalk/75 hover:text-chalk transition-colors duration-300'
                >
                  <span className='flip-label'>
                    <span>{item.label}</span>
                    <span aria-hidden='true'>{item.label}</span>
                  </span>
                </button>
              ))}
            </nav>
          </div>

          <div>
            <div className='label-mono text-chalk/45 mb-4'>Elsewhere</div>
            <nav className='flex flex-col gap-2.5'>
              {SOCIALS.map(item => (
                <a
                  key={item.label}
                  href={item.href}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='group w-fit text-sm text-chalk/75 hover:text-chalk transition-colors duration-300 inline-flex items-center gap-1.5'
                >
                  <span className='flip-label'>
                    <span>{item.label}</span>
                    <span aria-hidden='true'>{item.label}</span>
                  </span>
                  <ArrowUpRight
                    size={13}
                    className='text-chalk/45 group-hover:text-accent group-hover:rotate-45 transition-all duration-300'
                  />
                </a>
              ))}
            </nav>
          </div>

          <div>
            <div className='label-mono text-chalk/45 mb-4'>Direct</div>
            <div className='flex flex-col gap-2.5 text-sm text-chalk/75'>
              <a href={`mailto:${LINKS.email}`} className='link-line hover:text-chalk break-all'>
                {LINKS.email}
              </a>
              <a href={LINKS.phoneHref} className='link-line w-fit hover:text-chalk'>
                {LINKS.phone}
              </a>
              <span className='label-mono !text-[10px] text-chalk/45 mt-1'>Kerala, India (IST)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Giant mosaic wordmark */}
      <div className='max-w-7xl mx-auto px-5 sm:px-6 pt-6 pb-12'>
        <PixelWordmark text='AKASH ROY' />
      </div>

      {/* Bottom bar */}
      <div className='border-t border-chalk/10'>
        <div
          suppressHydrationWarning
          className='max-w-7xl mx-auto px-5 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 label-mono !text-[10px] text-chalk/45'
        >
          <span>© {new Date().getFullYear()} Akash Roy</span>
          <span>Made with ♡ by Akash Roy | Andaman & Nicobar Islands</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
